import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { generatePath, useLocation, useNavigate, useRevalidator } from 'react-router';
import { EcommerceEventTypeV4 } from '../../common/analytics';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { DialogType, SubscriptionAddonCode, SubscriptionCategory } from '../../common/enums';
import { changeSubscriptionFeatures } from '../../common/fetch';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { somethingWentWrongMsg, t } from '../../common/i18n';
import { getSubscriptionUrlId, isReachabilityWithMobile } from '../../common/utils/subscriptionUtils';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { openDialog } from '../../public/site/dialogStore.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { getReachabilityWithMobilePayload, pushAnalyticsEvent } from '../PbxAccordionContent/PbxAccordionUtils';
import {
  calculateServiceFee,
  calculateUpdatedPrice,
  checkCurrentCombination,
  getAddOnPrice,
  getAddOnsByCodes,
  reachabilityDefaultValues,
} from '../SubscriptionDetails/addOnDependencyUtils';
import { AddonConfirmationDialog } from './AddonConfirmationDialog';
import type { DialogParams } from '../../common/types/dialog';
import type { AddOnVisibility } from '../../generated/api/addOnVisibility';
import type { Subscription } from '../../generated/api/subscription';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { BaseRules } from '../SubscriptionDetails/addOnDependencyUtils';

interface AddReachabilityWithMobileAddonProps {
  subscription: Subscription;
  addOnRulesAssociations?: AssociationRecord[];
  addOnRulesDependencies?: DependencyRecord[];
  addOnVisibilities?: AddOnVisibility[];
  hasPendingActions: boolean;
  analyticsBundleId: string;
  whiteButton?: boolean;
}

const checkIsAddonCombinationValid = (
  subscription: Subscription,
  addOnRulesAssociations: AssociationRecord[] | undefined,
  addOnRulesDependencies: DependencyRecord[] | undefined,
  addOnVisibilities: AddOnVisibility[] | undefined
) => {
  const subscriptionAddOnCodes = subscription.details?.selectedAddOns?.map(addOn => addOn.addOnCode) || [];
  const baseRules: BaseRules = {
    commercialProductId: subscription.commercialProductId || '',
    commercialProductCode: subscription.commercialProductCode || '',
    addOnAssociations: addOnRulesAssociations || [],
    addOnDependencies: addOnRulesDependencies || [],
  };
  const combination = checkCurrentCombination(
    baseRules,
    [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE],
    [],
    subscriptionAddOnCodes,
    addOnVisibilities || []
  );
  return (
    combination.isValid && addOnVisibilities?.some(visibility => isReachabilityWithMobile(visibility.addOn?.addOnCode))
  );
};

export const AddReachabilityWithMobileAddon = ({
  subscription,
  addOnRulesAssociations,
  addOnRulesDependencies,
  addOnVisibilities,
  hasPendingActions,
  analyticsBundleId,
  whiteButton = false,
}: AddReachabilityWithMobileAddonProps) => {
  const { mdmId } = useSearchParams<{ mdmId?: string }>();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const { search } = useLocation();
  const { revalidate } = useRevalidator();
  const onShowDialog = (params: DialogParams) => openDialog(params);

  const addOnVisibility = addOnVisibilities?.find(visibility => isReachabilityWithMobile(visibility.addOn?.addOnCode));
  const addedAddOns = getAddOnsByCodes(
    [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE],
    addOnVisibilities || [],
    subscription.details?.selectedAddOns
  );
  const priceInfo = { value: getAddOnPrice(addOnVisibility?.addOn?.price?.effectivePrice).text || '-' };
  const isCurrentCombinationValid = checkIsAddonCombinationValid(
    subscription,
    addOnRulesAssociations,
    addOnRulesDependencies,
    addOnVisibilities
  );

  const onConfirmAddReachabilityAddon = async () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      onShowDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return;
    }
    if (hasPendingActions) {
      onShowDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return;
    }
    const payload = getReachabilityWithMobilePayload(addedAddOns, [], [], subscription, {
      [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE]: reachabilityDefaultValues,
    });

    try {
      setIsSaving(true);
      await changeSubscriptionFeatures(payload, subscription.subscriptionId, mdmId);
      setIsSaving(false);
      pushAnalyticsEvent(addedAddOns, EcommerceEventTypeV4.PURCHASE, analyticsBundleId);
      addNotification(t.T29V('Change order successful'), 'success');
      navigate(
        generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(subscription) }) + search
      );
      // Refresh /pending-subscription-actions data to update subscription status
      revalidate();
    } catch (error) {
      addNotification(t.JDTO(somethingWentWrongMsg), 'error');
      setIsSaving(false);
    }
    setShowConfirmDialog(false);
  };

  return (
    <>
      <div className="of-pbx-additional_services__addon">
        <div className="of-pbx-additional_services__addon__name">
          <div className={`${dsClass.PADDING_BOTTOM_2} ${dsClass.PADDING_TOP_3}`}>
            {t.VSWS('Reachability with mobile number')}
          </div>
          <div className={`${dsClass.PADDING_BOTTOM_2} ${dsClass.PADDING_TOP_3} ${dsClass.PADDING_RIGHT_3}`}>
            {priceInfo?.value}
          </div>
        </div>

        <div className={`${dsClass.FONT_SIZE_SMALLER}`}>
          {t.WXYJ(
            'You can add backup respondents for the mobile phone number. Vakio’s call features are now available for calls to mobile phone numbers as well. '
          )}
          <CL.Link
            className={dsClass.FONT_SIZE_MEDIUM}
            linkTarget="_blank"
            linkHref="https://yrityksille.elisa.fi/ohjeet/vakio-vinkit"
          >
            {t.HKO6('Read more about reachability with mobile phone number')}
            <CL.Icon icon="external-link" />
          </CL.Link>
        </div>
        <div className={`${dsClass.PADDING_TOP_3} ${dsClass.PADDING_BOTTOM_3}`}>
          <CL.Button
            color={whiteButton ? 'secondary' : 'primary'}
            disabled={!isCurrentCombinationValid}
            onClick={() => {
              setShowConfirmDialog(true);
            }}
          >
            {t.XFA9('Activate for')} {priceInfo?.value}
          </CL.Button>
        </div>
      </div>
      {showConfirmDialog && (
        <AddonConfirmationDialog
          addedAddOns={addedAddOns}
          isEmployee={false}
          removedAddOns={[]}
          onCloseDialog={() => setShowConfirmDialog(false)}
          onConfirm={onConfirmAddReachabilityAddon}
          serviceFee={calculateServiceFee(addedAddOns)}
          updatedPrice={calculateUpdatedPrice(addedAddOns, [], SubscriptionCategory.VOICE, subscription)}
          isSaving={isSaving}
        />
      )}
    </>
  );
};
